import { GridContent } from '@ant-design/pro-components';
import { useSearchParams, history } from '@umijs/max';
import {
  Card,
  Radio,
  Button,
  Typography,
  Space,
  Spin,
  message,
  Alert,
  Result,
} from 'antd';
import {
  WechatOutlined,
  QrcodeOutlined,
} from '@ant-design/icons';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  createPaymentOrder,
  capturePayment,
  getPaymentMethods,
  getPaymentStatus,
  getPlans,
} from '@/services/payment';
import { createStyles } from 'antd-style';
import IconPayPal from '@/assets/icons/IconPayPal.svg';
import { useRequest } from '@umijs/max';
import { extractPlanTabs, flattenPlanTabs } from '@/pages/payment/utils';

const { Title, Text } = Typography;

const PAYPAL_RETURN_MESSAGE_TYPE = 'nexus_payment_paypal_return';

const useStyles = createStyles(({ token }) => ({
  container: {
    maxWidth: 640,
    margin: '0 auto',
    padding: '24px 0',
  },
  summaryCard: {
    borderRadius: token.borderRadiusXL,
  },
  summaryMeta: {
    display: 'grid',
    gridTemplateColumns: '120px 1fr',
    rowGap: 16,
    columnGap: 0,
  },
  summaryLabel: {
    color: token.colorTextSecondary,
    fontWeight: 500,
  },
  summaryValue: {
    color: token.colorText,
    fontWeight: 600,
  },
  amountPanel: {
    marginTop: 6,
    padding: '16px 18px',
    borderRadius: token.borderRadiusLG,
    background: token.colorFillAlter,
  },
  amountPrimary: {
    fontSize: 32,
    lineHeight: 1.1,
    fontWeight: 800,
    color: token.colorText,
  },
  settlementBlock: {
    marginTop: 10,
    paddingTop: 10,
    borderTop: `1px solid ${token.colorBorderSecondary}`,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 4,
  },
  qrWrapper: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    padding: 24,
  },
  methodOption: {
    padding: '12px 16px',
    borderRadius: token.borderRadiusLG,
    border: `1px solid ${token.colorBorder}`,
    marginBottom: 8,
    width: '100%',
  },
  noticeCard: {
    borderRadius: token.borderRadiusXL,
    border: `1px solid ${token.colorInfoBorder}`,
    background: token.colorInfoBg,
    padding: '18px 20px',
  },
  noticeHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flexWrap: 'wrap' as const,
    gap: 16,
    marginBottom: 8,
  },
  noticeTitle: {
    fontSize: 17,
    lineHeight: 1.4,
    fontWeight: 700,
    color: token.colorText,
  },
  noticeDesc: {
    color: token.colorText,
    lineHeight: 1.75,
    fontSize: 14,
  },
}));

const MAX_SSE_RETRIES = 10;

type CheckoutContext =
  | { orderType: 'SUBSCRIPTION'; planCode: string; planName?: string; billingCycle: string; durationDays?: number }
  | { orderType: 'RECHARGE'; rechargeAmount: number; currency?: string; paymentMethod?: string };

type CheckoutProps = { embedded?: boolean; ctx?: CheckoutContext; onDone?: () => void };

const formatCurrencyAmount = (currency?: string, amount?: number | string | null) => {
  const symbol = String(currency || '').toUpperCase() === 'USD' ? '$' : '¥';
  return `${symbol}${Number(amount ?? 0).toFixed(2)}`;
};

const resolveOrderErrorMessage = (error: any) => {
  const messageCandidates = [
    error?.response?.data?.message,
    error?.response?.data?.error?.message,
    error?.data?.message,
    error?.data?.error?.message,
    error?.info?.message,
    error?.info?.error?.message,
  ];
  const messageFromResponse = messageCandidates.find(
    (item) => typeof item === 'string' && item.trim(),
  );
  if (messageFromResponse) return messageFromResponse;
  return error?.message || '创建订单失败';
};

function CheckoutEmbedded({ embedded, ctx, onDone }: CheckoutProps) {
  const { styles } = useStyles();
  // Embedded mode is rendered via `createRoot` and is NOT inside the router context.
  // Do NOT use router hooks here.
  const orderType = ctx?.orderType || '';
  const planCode = ctx && ctx.orderType === 'SUBSCRIPTION' ? ctx.planCode : '';
  const planNameFromCtx = ctx && ctx.orderType === 'SUBSCRIPTION' ? ctx.planName : undefined;
  const billingCycle = ctx && ctx.orderType === 'SUBSCRIPTION' ? ctx.billingCycle : '';
  const durationDaysFromCtx = ctx && ctx.orderType === 'SUBSCRIPTION' ? ctx.durationDays : undefined;
  const rechargeAmount = ctx && ctx.orderType === 'RECHARGE' ? String(ctx.rechargeAmount) : '';
  const preselectedMethod = ctx && ctx.orderType === 'RECHARGE' ? (ctx.paymentMethod || '') : '';

  const [paymentMethod, setPaymentMethod] = useState<string>(preselectedMethod || 'WECHAT_NATIVE');
  const [methods, setMethods] = useState<any[]>([]);
  const [orderInfo, setOrderInfo] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [finalStatus, setFinalStatus] = useState<'success' | 'failed' | 'cancelled' | ''>('');
  const [paypalApprovalUrl, setPaypalApprovalUrl] = useState<string>('');
  const [paypalOrderNo, setPaypalOrderNo] = useState<string>('');
  const sseRef = useRef<EventSource | null>(null);
  const retryCountRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectedMethodInfo = methods.find((method) => method.code === paymentMethod);

  const { data: planMeta } = useRequest(
    async () => {
      const list = await getPlans({ skipErrorHandler: true });
      const match = flattenPlanTabs(extractPlanTabs(list)).find((p: any) =>
        String(p?.planCode || '').toLowerCase() === String(planCode || '').toLowerCase() &&
        String(p?.billingCycle || '').toUpperCase() === String(billingCycle || '').toUpperCase()
      );
      if (!match) return undefined;
      return {
        planName: match.planName,
        durationDays: match.durationDays,
      };
    },
    {
      ready: Boolean(orderType === 'SUBSCRIPTION' && planCode && billingCycle && !planNameFromCtx),
      cacheKey: planCode && billingCycle ? `payment-plan-meta-${planCode}-${billingCycle}` : undefined,
      staleTime: 15_000,
    },
  );

  const displayPlanName = planNameFromCtx || planMeta?.planName || planCode;
  const displayDurationDays = durationDaysFromCtx || planMeta?.durationDays;

  useEffect(() => {
    getPaymentMethods().then((data) => {
      if (Array.isArray(data)) {
        setMethods(data);
        // 若 URL 未指定支付方式，取第一个
        if (!preselectedMethod && data.length > 0) {
          setPaymentMethod(data[0].code);
        }
      }
    }).catch(() => {
      message.error('获取支付方式失败');
    });
    return () => {
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  // Embedded: listen for PayPal return bridge messages from the popup window.
  useEffect(() => {
    const onMessage = (ev: MessageEvent) => {
      if (!ev?.data || typeof ev.data !== 'object') return;
      const payload: any = ev.data;
      if (payload.type !== PAYPAL_RETURN_MESSAGE_TYPE) return;
      if (payload.orderNo && paypalOrderNo && String(payload.orderNo) !== String(paypalOrderNo)) return;
      const status = String(payload.status || '').toLowerCase();
      if (status === 'success') {
        setWaiting(false);
        setFinalStatus('success');
      } else if (status === 'failed') {
        setWaiting(false);
        setFinalStatus('failed');
      } else if (status === 'cancelled') {
        setWaiting(false);
        setFinalStatus('cancelled');
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [paypalOrderNo]);

  const handleCreateOrder = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
        orderType,
        paymentMethod,
      };
      if (orderType === 'SUBSCRIPTION') {
        params.planCode = planCode;
        params.billingCycle = billingCycle;
      } else {
        params.rechargeAmount = parseFloat(rechargeAmount);
      }

      const result = await createPaymentOrder(params, { skipErrorHandler: true });
      setOrderInfo(result);

      if (paymentMethod === 'PAYPAL' && result.approvalUrl) {
        setPaypalApprovalUrl(String(result.approvalUrl || ''));
        // PayPal popup flow (bridge mode):
        // PayPal returns to our site in the popup, we capture there and postMessage back.
        window.open(result.approvalUrl, '_blank');
        setPaypalOrderNo(String(result.orderNo || ''));
        setWaiting(true);
      } else if (paymentMethod === 'WECHAT_NATIVE' && result.qrCodeUrl) {
        connectSse(result.orderNo);
      }
    } catch (e: any) {
      message.error(resolveOrderErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [orderType, paymentMethod, planCode, billingCycle, rechargeAmount]);

  const connectSse = (orderNo: string) => {
    setWaiting(true);
    retryCountRef.current = 0;
    doConnectSse(orderNo);
  };

  const doConnectSse = (orderNo: string) => {
    const sse = new EventSource(`/api/payment/sse/${orderNo}`);
    sseRef.current = sse;

    sse.addEventListener('payment_success', () => {
      sse.close();
      sseRef.current = null;
      setWaiting(false);
      setFinalStatus('success');
    });

    sse.onerror = () => {
      sse.close();
      sseRef.current = null;

      if (retryCountRef.current < MAX_SSE_RETRIES) {
        retryCountRef.current += 1;
        // 重连前先查一次状态，避免在断连期间已完成支付
        getPaymentStatus(orderNo)
          .then((status) => {
            if (status?.status === 'PAID' || status?.status === 'DONE') {
              setWaiting(false);
              setFinalStatus('success');
            } else {
              timeoutRef.current = setTimeout(() => doConnectSse(orderNo), 2000);
            }
          })
          .catch(() => {
            timeoutRef.current = setTimeout(() => doConnectSse(orderNo), 2000);
          });
      } else {
        // 重试用尽，最终查一次状态
        setWaiting(false);
        getPaymentStatus(orderNo)
          .then((status) => {
            if (status?.status === 'PAID' || status?.status === 'DONE') {
              setFinalStatus('success');
            } else {
              message.warning('网络连接中断，请刷新页面或检查支付结果');
            }
          })
          .catch(() => {
            message.warning('网络连接中断，请刷新页面或检查支付结果');
          });
      }
    };
  };

  const methodIcons: Record<string, React.ReactNode> = {
    WECHAT_NATIVE: <WechatOutlined style={{ color: '#07c160', fontSize: 20 }} />,
    PAYPAL: <img src={IconPayPal} alt="PayPal" width={20} height={20} style={{ display: 'block', objectFit: 'contain' }} />,
  };
  const settlementAmount = orderInfo?.settlementAmount ?? orderInfo?.originalAmount;
  const settlementCurrency = orderInfo?.settlementCurrency ?? orderInfo?.originalCurrency;
  const hasSettlementInfo = settlementAmount != null && settlementCurrency;

  const content = (
      <div className={styles.container} style={{ padding: 0, maxWidth: embedded ? 720 : 820 }}>
        {!embedded && (
          <Title level={3} style={{ textAlign: 'center', marginBottom: 24 }}>
            确认支付
          </Title>
        )}

        {/* 订单摘要 */}
        <Card className={styles.summaryCard} style={{ marginBottom: 16 }}>
          <div className={styles.summaryMeta}>
            <div className={styles.summaryLabel}>订单类型</div>
            <div className={styles.summaryValue}>{orderType === 'SUBSCRIPTION' ? '订阅' : '充值'}</div>

            {orderType === 'SUBSCRIPTION' && (
              <>
                <div className={styles.summaryLabel}>套餐</div>
                <div className={styles.summaryValue}>{displayPlanName}</div>
                {displayDurationDays != null && (
                  <>
                    <div className={styles.summaryLabel}>套餐周期</div>
                    <div className={styles.summaryValue}>{displayDurationDays} 天</div>
                  </>
                )}
              </>
            )}

            {orderType === 'RECHARGE' && orderInfo?.totalCredits != null && (
              <>
                <div className={styles.summaryLabel}>到账总点数</div>
                <div className={styles.summaryValue}>
                  {Number(orderInfo.totalCredits || 0).toFixed(2)} 点
                </div>
              </>
            )}
          </div>

          {orderInfo?.amount != null && (
            <div className={styles.amountPanel}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                支付金额
              </Text>
              <div className={styles.amountPrimary}>
                {formatCurrencyAmount(orderInfo.currency, orderInfo.amount)}
              </div>
              {hasSettlementInfo && (
                <div className={styles.settlementBlock}>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    结算金额 {formatCurrencyAmount(settlementCurrency, settlementAmount)}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    {orderType === 'SUBSCRIPTION' ? '订阅权益按套餐配置发放' : '到账点数按结算金额计算'}
                  </Text>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* 支付方式展示/选择 */}
        {!orderInfo && (
          <Card title={preselectedMethod ? "支付方式" : "选择支付方式"} style={{ marginBottom: 16 }}>
            {preselectedMethod ? (
              <div className={styles.methodOption}>
                <Space>
                  {selectedMethodInfo ? methodIcons[selectedMethodInfo.code] : methodIcons[paymentMethod]}
                  <Text strong>{selectedMethodInfo?.name || paymentMethod}</Text>
                </Space>
              </div>
            ) : (
              <Radio.Group
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                style={{ width: '100%' }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  {methods.map((m: any) => (
                    <Radio key={m.code} value={m.code} className={styles.methodOption}>
                      <Space>
                        {methodIcons[m.code]}
                        <Text strong>{m.name}</Text>
                      </Space>
                    </Radio>
                  ))}
                </Space>
              </Radio.Group>
            )}
            {paymentMethod === 'PAYPAL' && (
              <Alert
                type="info"
                showIcon
                style={{ marginTop: 12 }}
                message="PayPal 使用 USD 支付"
                description="系统会按当前支付汇率换算并生成本次订单的结算金额；若换算后不足 0.01 USD，则无法完成支付。"
              />
            )}

            <Button
              type="primary"
              block
              size="large"
              style={{ marginTop: 16 }}
              loading={loading}
              disabled={methods.length === 0}
              onClick={handleCreateOrder}
            >
              确认支付
            </Button>
          </Card>
        )}

        {/* 微信支付状态卡：支付前显示二维码，支付后就地替换为结果 */}
        {(orderInfo?.qrCodeUrl || finalStatus) && (
          <Card>
            {!finalStatus && orderInfo?.qrCodeUrl && (
              <div className={styles.qrWrapper}>
                <QrcodeOutlined style={{ fontSize: 24, marginBottom: 8, color: '#07c160' }} />
                <Text strong style={{ marginBottom: 16 }}>
                  请使用微信扫码支付
                </Text>
                <QRCodeSVG value={orderInfo.qrCodeUrl} size={220} />
                {waiting && (
                  <Space style={{ marginTop: 16 }}>
                    <Spin size="small" />
                    <Text type="secondary">等待支付中...</Text>
                  </Space>
                )}
                {orderInfo.expireTime && (
                  <Text type="secondary" style={{ marginTop: 8, fontSize: 12 }}>
                    订单将于 30 分钟后过期
                  </Text>
                )}
              </div>
            )}

            {finalStatus === 'success' && (
              <Result
                status="success"
                title="支付成功"
                subTitle={orderInfo?.orderNo ? `订单号: ${orderInfo.orderNo}` : undefined}
                extra={[
                  <Button type="primary" key="close" onClick={() => onDone?.()}>
                    关闭
                  </Button>,
                ]}
              />
            )}

            {finalStatus === 'failed' && (
              <Result
                status="error"
                title="支付失败"
                subTitle={orderInfo?.orderNo ? `订单号: ${orderInfo.orderNo}` : undefined}
                extra={[
                  <Button type="primary" key="close" onClick={() => onDone?.()}>
                    关闭
                  </Button>,
                ]}
              />
            )}

            {finalStatus === 'cancelled' && (
              <Result
                status="warning"
                title="已取消支付"
                subTitle={orderInfo?.orderNo ? `订单号: ${orderInfo.orderNo}` : undefined}
                extra={[
                  <Button type="primary" key="close" onClick={() => onDone?.()}>
                    关闭
                  </Button>,
                ]}
              />
            )}
          </Card>
        )}

        {/* PayPal 跳转提示 */}
        {orderInfo?.approvalUrl && !finalStatus && (
          <div className={styles.noticeCard}>
            <div className={styles.noticeHeader}>
              <div>
                <div className={styles.noticeTitle}>请在新打开的 PayPal 页面完成支付</div>
                <div className={styles.noticeDesc}>
                  支付完成后，此页面会自动同步结果；如果浏览器拦截了新窗口，可点击右侧按钮重新打开。
                </div>
              </div>
              <Button
                type="link"
                style={{ paddingInline: 0, fontWeight: 600 }}
                onClick={() =>
                  window.open(
                    paypalApprovalUrl || orderInfo.approvalUrl,
                    '_blank',
                  )
                }
              >
                重新打开 PayPal
              </Button>
            </div>
          </div>
        )}
      </div>
  );

  return content;
}

function CheckoutRouted({ onDone }: CheckoutProps) {
  const { styles } = useStyles();
  const [searchParams] = useSearchParams();
  const embedded = false;

  const orderType = searchParams.get('orderType') || '';
  const planCode = searchParams.get('planCode') || '';
  const billingCycle = searchParams.get('billingCycle') || '';
  const rechargeAmount = searchParams.get('rechargeAmount') || '';
  const preselectedMethod = searchParams.get('paymentMethod') || '';

  // Reuse embedded implementation by mapping URL params to ctx.
  const ctx: any =
    orderType === 'SUBSCRIPTION'
      ? { orderType: 'SUBSCRIPTION', planCode, billingCycle }
      : { orderType: 'RECHARGE', rechargeAmount: Number(rechargeAmount || 0) || 0, paymentMethod: preselectedMethod };

  // PayPal return bridge (popup) still relies on query params.
  const [capturing, setCapturing] = useState(false);
  const [bridgeStatus, setBridgeStatus] = useState<'capturing' | 'success' | 'failed' | 'cancelled' | ''>('');
  const capturedRef = useRef(false);

  useEffect(() => {
    const paypalToken = searchParams.get('token');
    const orderNo = searchParams.get('orderNo');
    const cancelled = searchParams.get('cancelled');

    const inPopup = Boolean((window as any).opener);
    const postToOpener = (status: 'success' | 'failed' | 'cancelled') => {
      try {
        (window as any).opener?.postMessage(
          { type: PAYPAL_RETURN_MESSAGE_TYPE, orderNo, status },
          window.location.origin,
        );
      } catch {
        // ignore
      }
    };

    if (cancelled && orderNo) {
      if (inPopup) {
        setBridgeStatus('cancelled');
        postToOpener('cancelled');
        setTimeout(() => window.close(), 120);
        return;
      }
      history.push(`/payment/result?orderNo=${orderNo}&status=cancelled`);
      return;
    }

    if (paypalToken && orderNo && !capturedRef.current) {
      capturedRef.current = true;
      if (inPopup) {
        setBridgeStatus('capturing');
      } else {
        setCapturing(true);
      }
      capturePayment(orderNo)
        .then(() => {
          if (inPopup) {
            setBridgeStatus('success');
            postToOpener('success');
            setTimeout(() => window.close(), 120);
          } else {
            history.push(`/payment/result?orderNo=${orderNo}&status=success`);
          }
        })
        .catch(() => {
          if (inPopup) {
            setBridgeStatus('failed');
            postToOpener('failed');
            setTimeout(() => window.close(), 120);
          } else {
            history.push(`/payment/result?orderNo=${orderNo}&status=failed`);
          }
        });
    }
  }, [searchParams]);

  // PayPal capture 进行中的加载状态
  if (capturing) {
    return (
      <GridContent>
        <div className={styles.container}>
          <Result
            icon={<Spin size="large" />}
            title="正在确认支付..."
            subTitle="请稍候，正在处理 PayPal 支付结果"
          />
        </div>
      </GridContent>
    );
  }

  // PayPal bridge popup mode: minimal UI and auto-close
  if (bridgeStatus) {
    const statusToResult: any = bridgeStatus === 'capturing' ? 'info' : bridgeStatus;
    const title =
      bridgeStatus === 'capturing'
        ? '正在确认 PayPal 支付...'
        : bridgeStatus === 'success'
          ? '支付成功'
          : bridgeStatus === 'failed'
            ? '支付失败'
            : '已取消支付';
    return (
      <GridContent>
        <div className={styles.container}>
          <Result
            status={statusToResult}
            icon={bridgeStatus === 'capturing' ? <Spin size="large" /> : undefined}
            title={title}
            subTitle="此窗口将自动关闭"
            extra={[
              <Button key="close" type="primary" onClick={() => window.close()}>
                关闭窗口
              </Button>,
            ]}
          />
        </div>
      </GridContent>
    );
  }

  return (
    <GridContent>
      <CheckoutEmbedded ctx={ctx} onDone={onDone} embedded={embedded} />
    </GridContent>
  );
}

const Checkout: React.FC<CheckoutProps> = (props) => {
  if (props.embedded) {
    return <CheckoutEmbedded {...props} />;
  }
  return <CheckoutRouted {...props} />;
};

export default Checkout;
